import RequestContext from "./RequestContext";
import ApiDef from "./ApiDef";
import { RawAxiosResponseHeaders } from "axios";

export type QueryValue = any;

export type Params = string;
export type Query = Record<string, QueryValue | Array<QueryValue>>;
export type Body = Record<string, any>;

export interface ApiResponse<T = any> {
	status: number;
	data: T;
	headers: RawAxiosResponseHeaders;
}

export type Headers = Readonly<Record<string, string>>;

export interface BaseRequestConfig<
	R = any,
	P extends Params | undefined = Params,
	Q extends Query | undefined = Query,
	B extends Body | undefined = Body,
> {
	options?: RequestConfigOptions<R, P, Q, B>;
	headers?: Headers;
}

export type RequestConfig<
	R = any,
	P extends Params | undefined = Params,
	Q extends Query | undefined = Query,
	B extends Body | undefined = Body,
> = (P extends undefined
	? { params?: never }
	: { params: Record<P extends Params ? P : never, string> }) &
	(Q extends undefined ? { query?: never } : { query: Q }) &
	(B extends undefined ? { body?: never } : { body: B }) &
	BaseRequestConfig<R, P, Q, B>;

export interface RequestConfigOptions<
	R = any,
	P extends Params | undefined = Params,
	Q extends Query | undefined = Query,
	B extends Body | undefined = Body,
> {
	cache?: number | boolean;
	lock?:
		| string
		| false
		| ((context: RequestContext<R, P, Q, B>) => string | false);
	maxRetries?: number;
}

export enum RequestMethod {
	Post = "post",
	Get = "get",
}

export enum RequestEvent {
	BeforeSend = "beforeSend",
	Success = "success",
	Error = "error",
	UnrecoverableError = "unrecoverableError",
}

export enum EventResultType {
	Respond = "respond",
	Retry = "retry",
	Cancel = "cancel",
}

interface BaseEventResult<T extends EventResultType> {
	type: T;
}

export type ResponseEventResult<R> =
	BaseEventResult<EventResultType.Respond> & {
		response: ApiResponse<R>;
	};

export type RetryEventResult<R> = BaseEventResult<EventResultType.Retry>;

export type CancelEventResult<R> = BaseEventResult<EventResultType.Cancel>;

export type EventResult<R> =
	| CancelEventResult<R>
	| ResponseEventResult<R>
	| RetryEventResult<R>;

export type RequestEventHandler<R> = (
	context: RequestContext<R>,
) => EventResult<R> | void | Promise<EventResult<R> | void>;

export type RequestEventHandlers<R> = {
	[key in RequestEvent]?: Array<RequestEventHandler<R>>;
};

export type RequestMiddleware<O = undefined> = {
	[key in RequestEvent]?: RequestEventHandler<any> | undefined | false;
};

export interface RequestContextStats {
	cached: false | { is: true; by: "local" | "api" };
	attempt: number;
}

export interface RequestError extends Error {
	name: "RequestError";
	response?: ApiResponse;
}

export interface RequestHost {
	readonly method: RequestMethod;
	readonly api: ApiDef;
	readonly baseUrl: string;
	readonly path: string;
	readonly responseType: ResponseType;

	computeConfig<
		R,
		P extends Params | undefined,
		Q extends Query | undefined,
		B extends Body | undefined,
	>(
		config: RequestConfig<R, P, Q, B>,
	): RequestConfig<R, P, Q, B>;

	computePath(path: string, config: RequestConfig): string;
}

export interface CancelledRequestError extends Error {
	isCancelledRequest: true;
}

export enum CacheSource {
	Api = "api",
	Local = "local",
}

export interface RequestCacheInfo {
	cached: boolean;
	source: CacheSource | null;
}

export enum ResponseType {
	Json = "json",
	Text = "text",
	ArrayBuffer = "arraybuffer",
}
